<template>
  <v-expansion-panels
    accordion
    class="stats-panels"
  >
    <v-expansion-panel
      :disabled="!projectValid"
      class="stats-expansion-panel"
      elevation="1"
    >
      <v-expansion-panel-title class="stats-panel-title">
        <div class="stats-title-content">
          <div class="stats-title-left">
            <div
              class="stats-title-icon"
              :class="{ 'stats-title-icon-disabled': !projectValid }"
            >
              <v-icon
                :color="projectValid ? 'primary' : undefined"
                size="24"
              >
                {{
                  projectValid
                    ? 'mdi-chart-multiple'
                    : 'mdi-alert-circle-outline'
                }}
              </v-icon>
            </div>

            <div>
              <div class="stats-heading">
                {{
                  projectValid
                    ? 'Energiediagramme'
                    : 'Vorschau nicht verfügbar'
                }}
              </div>

              <div class="stats-subheading">
                <template v-if="projectValid">
                  Energiepreise, Emissionen, Verbrauch und Kosten auswerten.
                </template>

                <template v-else>
                  Bitte vervollständigen Sie zuerst die erforderlichen
                  Projektparameter.
                </template>
              </div>
            </div>
          </div>

          <v-chip
            v-if="projectValid"
            color="primary"
            variant="tonal"
            size="small"
            class="current-chart-chip d-none d-sm-inline-flex"
          >
            <v-icon start size="17">
              {{ currentTabIcon }}
            </v-icon>

            {{ slideTitle }}
          </v-chip>
        </div>
      </v-expansion-panel-title>

      <v-expansion-panel-text>
        <div class="stats-panel-content">
          <!-- Navigation -->
          <div class="stats-tabs-wrapper">
            <v-tabs
              v-model="slide"
              color="primary"
              density="comfortable"
              class="stats-tabs"
              show-arrows
            >
              <v-tab
                v-for="tab in tabs"
                :key="tab.value"
                :value="tab.value"
                :prepend-icon="tab.icon"
                class="stats-tab"
              >
                {{ tab.title }}
              </v-tab>
            </v-tabs>
          </div>

          <!-- API error -->
          <v-alert
            v-if="loadError"
            type="error"
            variant="tonal"
            border="start"
            class="stats-message"
          >
            <template #title>
              Diagramme konnten nicht geladen werden
            </template>

            {{ loadError }}
          </v-alert>

          <!-- Loading -->
          <div v-else-if="loading" class="stats-loading-state">
            <v-skeleton-loader
              type="heading, image"
              class="stats-skeleton"
            />
          </div>

          <!-- Content -->
          <v-window
            v-else
            v-model="slide"
            class="stats-window"
            :transition="false"
            :reverse-transition="false"
          >
            <v-window-item :value="0" >
              <StatsPrice
                :data="statsData?.energy_data?.price"
                :options="chartOptions"
                :colors="colors"
                :default-color="defaultColor"
                :project-name="projectName"
              />
            </v-window-item>

            <v-window-item :value="1" >
              <StatsCo2
                :data="statsData?.energy_data?.co2"
                :options="chartOptions"
                :colors="colors"
                :default-color="defaultColor"
                :project-name="projectName"
              />
            </v-window-item>

            <v-window-item :value="2" >
              <StatsPerformance
                :data="statsData?.energy_data?.performance"
                :options="chartOptions"
                :colors="colors"
                :default-color="defaultColor"
                :project-name="projectName"
              />
            </v-window-item>

            <v-window-item :value="3" >
              <StatsConsumption
                :data="statsData?.consumption?.consumption"
                :options="chartOptions"
                :colors="colors"
                :default-color="defaultColor"
                :project-name="projectName"
              />
            </v-window-item>

            <v-window-item :value="4" >
              <StatsCost
                :data="statsData?.consumption?.cost"
                :options="chartOptions"
                :colors="colors"
                :default-color="defaultColor"
                :project-name="projectName"
              />
            </v-window-item>

            <v-window-item :value="5" >
              <StatsHistoricPrice
                :data="statsData?.consumption?.historic_price"
                :options="chartOptions"
                :colors="colors"
                :default-color="defaultColor"
                :project-name="projectName"
              />
            </v-window-item>
          </v-window>
        </div>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useTheme } from 'vuetify'
import api from '@/plugins/axios'
import '@/assets/StyleMethodHelp.css'

import StatsPrice from './StatsPrice.vue'
import StatsCo2 from './StatsCo2.vue'
import StatsPerformance from './StatsPerformance.vue'
import StatsConsumption from './StatsConsumption.vue'
import StatsCost from './StatsCost.vue'
import StatsHistoricPrice from './StatsHistoricPrice.vue'

const props = defineProps({
  projectData: {
    type: Object,
    required: true,
  },
  projectValid: {
    type: [Boolean, Number],
    required: true,
  },
  projectName: {
    type: String,
    default: '',
  },
})

const vuetifyTheme = useTheme()

const slide = ref(0)
const statsData = ref(null)
const loading = ref(false)
const loadError = ref('')

const tabs = [
  {
    value: 0,
    title: 'Preise',
    icon: 'mdi-currency-eur',
  },
  {
    value: 1,
    title: 'CO₂',
    icon: 'mdi-cloud-outline',
  },
  {
    value: 2,
    title: 'Performance',
    icon: 'mdi-speedometer',
  },
  {
    value: 3,
    title: 'Verbrauch',
    icon: 'mdi-fire',
  },
  {
    value: 4,
    title: 'Kosten',
    icon: 'mdi-receipt-text-outline',
  },
  {
    value: 5,
    title: 'Historische Preise',
    icon: 'mdi-history',
  },
]

const currentTab = computed(() => {
  return tabs.find(tab => tab.value === slide.value)
})

const slideTitle = computed(() => {
  return currentTab.value?.title ?? ''
})

const currentTabIcon = computed(() => {
  return currentTab.value?.icon ?? 'mdi-chart-bar'
})

const isDark = computed(() => {
  return vuetifyTheme.global.current.value.dark
})

const themeColors = computed(() => {
  const colors = vuetifyTheme.global.current.value.colors

  return {
    tick: colors['on-surface'],
    grid: isDark.value
      ? 'rgba(232, 237, 245, 0.12)'
      : 'rgba(16, 32, 51, 0.09)',
    labelBackground: colors.primary,
  }
})

/*
 * Reactive options ensure that Chart.js receives new
 * colors when the active Vuetify theme changes.
 */
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  resizeDelay: 150,

  animation: {
    duration: 300,
    easing: 'easeOutQuart',
  },

  layout: {
    padding: {
      top: 12,
      right: 10,
      bottom: 8,
      left: 10,
    },
  },

  plugins: {
    title: {
      display: true,
      text: currentChartTitle.value,
      color: themeColors.value.tick,
      font: {
        weight: '600',
        size: 14,
      },
      padding: {
        top: 4,
        bottom: 12,
      },
    },

    legend: {
      display: false,
    },

    tooltip: {
      enabled: true,
      backgroundColor: isDark.value
        ? 'rgba(24, 30, 41, 0.96)'
        : 'rgba(255, 255, 255, 0.97)',
      titleColor: themeColors.value.tick,
      bodyColor: themeColors.value.tick,
      borderColor: isDark.value
        ? 'rgba(232, 237, 245, 0.16)'
        : 'rgba(16, 32, 51, 0.12)',
      borderWidth: 1,
      padding: 10,
      callbacks: {
        label(context) {
          const value = Number(context.raw)

          if (!Number.isFinite(value)) return ''

          return `${value.toLocaleString('de-DE')} ${currentChartUnit.value}`
        },
      },
    },

    datalabels: {
      formatter(value) {
        const number = Number(value)

        if (!Number.isFinite(number)) return ''

        return number.toLocaleString('de-DE', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      },

      color: '#ffffff',
      backgroundColor: themeColors.value.labelBackground,
      borderRadius: 6,

      font: {
        weight: 'bold',
      },

      offset: 2,
      padding: 4,
      anchor: 'center',
      align: 'center',
    },

    fancyBarShadow: {
      blur: isDark.value ? 5 : 8,
      color: isDark.value
        ? 'rgba(0, 0, 0, 0.28)'
        : 'rgba(0, 0, 0, 0.15)',
      offsetX: 0,
      offsetY: 4,
    },
  },

  scales: {
    x: {
      ticks: {
        display: true,
        color: themeColors.value.tick,
        maxRotation: 40,
        minRotation: 0,
      },

      grid: {
        display: false,
      },
    },

    y: {
      beginAtZero: true,

      title: {
        display: true,
        text: currentChartUnit.value,
        color: themeColors.value.tick,
      },

      ticks: {
        display: true,
        color: themeColors.value.tick,

        callback(value) {
          return Number(value).toLocaleString('de-DE')
        },
      },

      grid: {
        color: themeColors.value.grid,
      },
    },
  },
}))

const currentChartTitle = computed(() => {
  switch (slide.value) {
    case 0:
      return 'Energiepreise'
    case 1:
      return 'CO₂-Äquivalente'
    case 2:
      return 'Performance'
    case 3:
      return 'Energieverbrauch'
    case 4:
      return 'Energiekosten'
    case 5:
      return 'Historische Energiepreise'
    default:
      return ''
  }
})

const currentChartUnit = computed(() => {
  switch (slide.value) {
    case 0:
      return '€/kWh'
    case 1:
      return 'g/kWh'
    case 2:
      return ''
    case 3:
      return 'kWh/a'
    case 4:
      return '€/a'
    case 5:
      return '€/kWh'
    default:
      return ''
  }
})

const colors = {
  1: '#F7BA0B',
  2: '#D4652F',
  3: '#D4652F',
  4: '#904684',
  5: '#7B5141',
  6: '#9C6B30',
  7: '#8D1D2C',
  10: '#EEC900',
  11: '#A02128',
  12: '#2E5978',
  13: '#48A43F',
}

const defaultColor = '#9EA0A1'

async function loadStats() {
  if (!props.projectValid) {
    statsData.value = null
    loadError.value = ''
    return
  }

  loading.value = true
  loadError.value = ''

  try {
    const { data } = await api.post(
      '/projects/stats/',
      props.projectData
    )

    statsData.value = data
  } catch (error) {
    statsData.value = null

    loadError.value =
      error?.response?.data?.detail ||
      'Beim Laden der Energiediagramme ist ein Fehler aufgetreten.'
  } finally {
    loading.value = false
  }
}

/*
 * One immediate watcher is sufficient.
 * No additional onMounted(loadStats) is required.
 */
watch(
  [
    () => props.projectData,
    () => props.projectValid,
  ],
  loadStats,
  {
    immediate: true,
    deep: true,
  }
)
</script>

<style scoped lang="scss">
.stats-panels {
  border-radius: 24px;
}

.stats-expansion-panel {
  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 24px !important;

  color: rgb(var(--v-theme-on-surface));
  background-color: rgb(var(--v-theme-surface));
}

.stats-panel-title {
  min-height: 78px;
  padding: 1rem 1.25rem;

  background: linear-gradient(
    180deg,
    rgba(var(--v-theme-primary), 0.07),
    rgba(var(--v-theme-primary), 0.025)
  );
}

.stats-title-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  width: 100%;
  padding-right: 0.75rem;
}

.stats-title-left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.stats-title-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 46px;
  height: 46px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.09);
}

.stats-title-icon-disabled {
  background-color: rgba(var(--v-theme-on-surface), 0.06);
}

.stats-heading {
  color: rgb(var(--v-theme-on-surface));
  font-size: 1.05rem;
  font-weight: 700;
}

.stats-subheading {
  margin-top: 0.15rem;

  color: rgba(var(--v-theme-on-surface), 0.65);
  font-size: 0.82rem;
  line-height: 1.35;
}

.current-chart-chip {
  flex-shrink: 0;
}

.stats-panel-content {
  padding-top: 0.5rem;
}

.stats-tabs-wrapper {
  overflow: hidden;

  margin-bottom: 1rem;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 18px;

  background-color: rgba(var(--v-theme-primary), 0.025);
}

.stats-tabs {
  max-width: 100%;
}

.stats-tab {
  min-width: 128px;
  text-transform: none;
  font-weight: 600;
}

.stats-window {
  min-height: 410px;
}

.stats-message {
  margin-bottom: 1rem;
  border-radius: 18px;
}

.stats-loading-state {
  min-height: 390px;
  padding: 1rem;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 22px;

  background-color: rgba(var(--v-theme-primary), 0.02);
}

.stats-skeleton {
  border-radius: 18px;
}

@media (max-width: 700px) {
  .stats-panel-title {
    min-height: 72px;
    padding: 0.85rem;
  }

  .stats-title-content {
    align-items: flex-start;
  }

  .stats-title-left {
    align-items: flex-start;
  }

  .stats-title-icon {
    width: 40px;
    height: 40px;
  }

  .stats-subheading {
    display: none;
  }

  .stats-tab {
    min-width: 112px;
  }

  .stats-window {
    min-height: 360px;
  }
}
</style>
`